import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {
  GetAllCategoryClient,
  DeleteCategoryClient,
  UpdateCategoryClient,
  CreateCategoryClient,
  UpdateRecordCategoryClient,
  CreateRecordCategoryClient,
  GetRecordCategoryClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

const getAllCategoryClient = new GetAllCategoryClient(SERVICE_URL)
const createCategoryClient = new CreateCategoryClient(SERVICE_URL)
const deleteCategoryClient = new DeleteCategoryClient(SERVICE_URL)
const updateCategoryClient = new UpdateCategoryClient(SERVICE_URL)
const createRecordCategoryClient = new CreateRecordCategoryClient(SERVICE_URL)
const getRecordCategoryClient = new GetRecordCategoryClient(SERVICE_URL)
const updateRecordCategoryClient = new UpdateRecordCategoryClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  category: null,
  detail: null,
  recordCategory: null,
}

// Async Thunks
export const categoryGetAll = createAsyncThunk(
  'category/getAll',
  async (categoryTypeId, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getAllCategoryClient.getAll(categoryTypeId)
      dispatch(setCategoryList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const categoryCreate = createAsyncThunk('category/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await createCategoryClient.create(params)
    dispatch(setCategoryCreated(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const categoryUpdate = createAsyncThunk('category/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await updateCategoryClient.update(params)
    dispatch(setCategoryUpdated({category: response, params}))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const categoryDelete = createAsyncThunk('category/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await deleteCategoryClient.delete(params)
    dispatch(setCategoryDeleted(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

// Record Category Async Thunks
export const recordcategoryGet = createAsyncThunk(
  'category/recordcategoryGet',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await getRecordCategoryClient.get(params)
      dispatch(setRecordCategoryDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const recordcategoryCreate = createAsyncThunk(
  'category/recordcategoryCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await createRecordCategoryClient.create(params)
      dispatch(setRecordCategoryCreated(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const recordcategoryUpdate = createAsyncThunk(
  'category/recordcategoryUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateRecordCategoryClient.update(params)
      dispatch(setRecordCategoryUpdated({recordCategory: response, params}))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setCategoryList: (state, action) => {
      state.category = action.payload
      state.loading = false
      state.isRedirect = false
    },
    setCategoryDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
      state.isRedirect = false
    },
    setCategoryDeleted: (state) => {
      state.isDeleted = true
      state.loading = false
    },
    setCategoryCreated: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setCategoryUpdated: (state, action) => {
      state.isRedirect = action.payload.category || action.payload.params
      state.detail = action.payload.params
      state.loading = false
    },
    setRecordCategoryDetail: (state, action) => {
      state.recordCategory = action.payload
      state.loading = false
    },
    setRecordCategoryCreated: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setRecordCategoryUpdated: (state, action) => {
      state.isRedirect = action.payload.recordCategory || action.payload.params
      state.detail = action.payload.params
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('category/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

export const {
  setLoading,
  setError,
  setCategoryList,
  setCategoryDetail,
  setCategoryDeleted,
  setCategoryCreated,
  setCategoryUpdated,
  setRecordCategoryDetail,
  setRecordCategoryCreated,
  setRecordCategoryUpdated,
  resetState,
} = categorySlice.actions

// Selectors
export const selectCategoryList = (state) => state.category.category
export const selectCategoryLoading = (state) => state.category.loading
export const selectCategoryError = (state) => state.category.error
export const selectCategoryDetail = (state) => state.category.detail
export const selectRecordCategory = (state) => state.category.recordCategory

export default categorySlice.reducer
